<template lang="pug">
.send-message-history-table-component
  // テーブル
  b-table(
    :items="_value"
    :fields="fields"
    :current-page="currentPage"
    :per-page="perPage"
    :filter="filter"
    :busy="isLoading"
    sticky-header
    no-border-collapse
    table-class="basic-table"
    show-empty
    :filter-function="filterTable"
    @filtered="onFiltered"
  )
    // ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    // レコード無し
    template(#empty)
      .empty-message
        span 情報がありません
    // フィルター後レコード無し
    template(#emptyfiltered)
      .empty-message
        span 情報がありません

    // セル：日時
    template(#cell(datetime)="row")
      span {{ getDisplayDate(row.item) }}
    // セル：宛先
    template(#cell(receivers)="row")
      .overflow-hidden.receivers(:id="`receivers-${row.item.sortKey}`") {{ row.item.receivers }}
      b-popover(v-if="row.item.receivers" :target="`receivers-${row.item.sortKey}`" triggers="hover" :placement="'bottomright'")
        .d-flex.flex-column
          span(style="white-space: pre-wrap;") {{ formatPopoverList(row.item.receivers) }}
    // セル：タイトル
    template(#cell(title)="row")
      .overflow-hidden.title(:id="`title-${row.item.sortKey}`") {{ row.item.functionContents.title }}
      b-popover(v-if="row.item.functionContents.title" :target="`title-${row.item.sortKey}`" triggers="hover" :placement="'bottomright'")
        .d-flex.flex-column
          span {{ `${row.item.functionContents.title}` }}
    // セル：未読家族
    template(#cell(unread)="row")
      .d-flex.flex-row.justify-content-center.align-items-center(v-if="row.item.functionContents.addReadButton")
        .unread-count
          b-spinner.spinner(v-if="row.item.unreadCount == undefined")
          span(v-else) {{ row.item.unreadCount }} 人
        .unread-detail
          button.btn.detail-btn.disable(v-if="!row.item.unreadCount" disabled) 詳細
          button.btn.detail-btn(v-else @click="onDetail(row.item)") 詳細
      .unread.text-center(v-else)
        span 未読チェック無し
    // セル：詳細
    template(#cell(detail)="row")
      button.btn.confirm-btn.mr-2(@click="onConfirm(row.item)") 確認
      .send-message-badge(v-show="row.item.replyUnread")
      button.btn.delete-btn(@click="onDelete(row.item)") 削除
</template>

<script lang="ts">
import { SendMessageFunction } from "@/commons/models/Message";
import { SenderType } from "@/commons/models/Resident";
import { ReceiverDetail } from "@/commons/models/SendLineMessage";
import { Component, Prop, Vue } from "vue-property-decorator";

/**
 * メッセージ送信[履歴]テーブルコンポーネント
 */
@Component({})
export default class MessageHistoryTable extends Vue {
  /** 1行あたりの情報配列 */
  @Prop({ required: true })
  value!: Array<MessageHistoryTableItem>;
  /** 列名配列 */
  @Prop({ required: true })
  fields!: Array<MessageHistoryTableField>;
  /** 全体行数 */
  @Prop({ required: true })
  totalRows!: number;
  /** 現在のページ */
  @Prop({ required: true })
  currentPage!: number;
  /** 1ページあたりの表示行数 */
  @Prop({ required: true })
  perPage!: number;
  /** ローディング表示可否 */
  @Prop({ required: true })
  isLoading!: boolean;
  /** フィルター */
  @Prop()
  filter!: FilterSetting;

  get _value(): Array<MessageHistoryTableItem> {
    return this.value;
  }

  /**
   * ### フィルタリング
   */
  public filterTable(row: MessageHistoryTableItem, filter: FilterSetting): boolean {
    return this.filterWriterId(row, filter) && this.filterSearchWords(row, filter) && this.filterUnreadReply(row, filter);
  }

  public filterWriterId(row: MessageHistoryTableItem, filter: FilterSetting): boolean {
    if (filter.writerId == "") {
      return true;
    }

    if (row.writerId == filter.writerId) {
      return true;
    } else {
      return false;
    }
  }

  public filterSearchWords(row: MessageHistoryTableItem, filter: FilterSetting): boolean {
    if (filter.searchWord == "") {
      return true;
    }

    const words: string[] = this.$filterUtil.createSearchWord(filter.searchWord);

    let foundReceivers: boolean = true;
    let foundTitle: boolean = true;

    for (const word of words) {
      if (row.receivers.indexOf(word) == -1) {
        foundReceivers = false;
      }
      if (row.functionContents.title.indexOf(word) == -1) {
        foundTitle = false;
      }
    }

    return foundReceivers || foundTitle;
  }

  public filterUnreadReply(item: MessageHistoryTableItem, filter: FilterSetting): boolean {
    // 未読の返信のみ表示ではない場合、常に表示対象
    if (filter.onlyUnreadReply == false) {
      return true;
    }
    // 未読の返信のみ表示の場合、未読メッセージ数が1以上で表示
    else {
      if (item.replyUnread != undefined && item.replyUnread == true) {
        return true;
      } else {
        return false;
      }
    }
  }

  /**
   * ### フィルタリングイベント
   */
  public onFiltered(filteredItems: any) {
    this.$emit("update:totalRows", filteredItems.length);
    return;
  }

  /**
   * ### 送信手段の文字列を返す
   */
  public getSenderType(message: MessageHistoryTableItem): string {
    if (message.senderType == SenderType.LINE) {
      return "LINE";
    } else if (message.senderType == SenderType.MAIL) {
      return "メール";
    } else {
      return "";
    }
  }

  /**
   * ### ポップオーバー用にリスト文字列を10件までにフォーマットする
   */
  public formatPopoverList(items: string | string[], limit: number = 10): string {
    let list: string[] = [];
    if (Array.isArray(items)) {
      list = items;
    } else if (typeof items === "string") {
      if (!items) return "";
      list = items.split("、");
    }

    if (list.length <= limit) {
      return list.join("、");
    }

    const truncated = list.slice(0, limit);
    const remaining = list.length - limit;
    return `${truncated.join("、")}\n…その他${remaining}件`;
  }

  /**
   * ### 画面表示用の日付を返す
   */
  public getDisplayDate(message: MessageHistoryTableItem): string {
    if (message.datetime) {
      return this.$dateUtil.convertNumberToString(Number(message.datetime), "mm月dd日 hh:MM");
    } else {
      return "";
    }
  }

  /**
   * ### 詳細ボタン押下イベント
   */
  public onDetail(item: MessageHistoryTableItem): void {
    this.$emit("onDetail", item);
    return;
  }

  /**
   * ### 確認ボタン押下イベント
   */
  public onConfirm(item: MessageHistoryTableItem): void {
    this.$emit("onConfirm", item);
    return;
  }

  /**
   * ### 削除ボタン押下イベント
   */
  public onDelete(item: MessageHistoryTableItem): void {
    this.$emit("onDelete", item);
    return;
  }
}

/** テーブルの列名の情報 */
export interface MessageHistoryTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface MessageHistoryTableItem {
  facilityId: number;
  sortKey: string;
  senderType: SenderType;
  datetime: number;
  receivers: string;
  functionContents: SendMessageFunction;
  writerId: string;
  writtenBy: string;
  unreadCount?: number;
  replyUnread?: boolean;
  receiverDetails: Array<ReceiverDetail>;
  /** メッセージを共有するか否か */
  isSharedMessages: boolean;
}

export interface FilterSetting {
  /** 記載者 */
  writerId: string;
  /** 検索ワード */
  searchWord: string;
  /** 未読の返信のみ表示 */
  onlyUnreadReply: boolean;
}
</script>

<style lang="scss" scoped>
.send-message-history-table-component {
  .detail-btn,
  .confirm-btn {
    width: 64px;
  }
  // 文字がtd要素の幅をはみ出した場合「…」で隠すスタイル
  .overflow-hidden {
    overflow: hidden !important;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  // 送信メッセージに返信未読がある場合のバッジ
  .detail {
    .send-message-badge {
      position: absolute;
      top: 3px;
      left: 65px;
      z-index: 1;
      content: url("../../../assets/sidemenu-sendmessage-incoming.png");
    }
  }
  // 未読家族は1セルの中に複数要素を格納しているのでサイズ等を指定
  .unread-count {
    word-break: break-all;
    width: 75px;
    text-align: center;
  }
  .unread-detail {
    width: 64px;
    text-align: center;
  }
}
</style>
